// --- React
import React, { useCallback } from 'react';
// --- Hooks
import { useDialogsActions } from '../hooks';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeaderCell,
    TableRow,
} from '@dtci/spellbook/Table';

/** * CopyrightForm Component
 *
 * This component is responsible for rendering the copyright list.
 *
 * @component
 * @param {Object} dtaa - The message object containing copyright information.
 * @returns {JSX.Element} The rendered CopyrightForm component.
 */
const CopyrightForm = ({ data }) => {
    const tableHeaders = ['Copyright Notice'];

    return (
        <div className="flexBox">
            {/* Render the table with headers and data */}
            <Table bordered="horizontal">
                <TableHead>
                    <TableRow>
                        {/* Render the headers of the table */}
                        {tableHeaders?.map((header, idx) => (
                            <TableHeaderCell key={header}>{header}</TableHeaderCell>
                        ))}
                    </TableRow>
                </TableHead>
                {/* Render the body of the table */}
                <TableBody>
                    {data?.copyrightNotices ? (
                        data?.copyrightNotices?.map((data, index) => (
                            <TableRow key={index}>
                                <TableCell>{data?.copyRightNotice}</TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow key={window.crypto.randomUUID()}>
                            <TableCell>No Copyright Notices</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
};

/**
 * useCopyrightDialog Hook
 *
 * This hook provides functionality to open a copyright dialog.
 * It uses the useDialogsActions hook to manage the dialog state.
 *
 * @returns {Object} An object containing the openCopyrightDialog function.
 */
export const useCopyrightDialog = () => {
    const { openDialog } = useDialogsActions();

    // Function to open the delete confirmation dialog
    const openCopyrightDialog = useCallback(
        (data) => {
            openDialog({
                title: 'Copyright Notices',
                content: <CopyrightForm data={data} />,
                closeAction: true,
            });
        },
        [openDialog],
    );

    return { openCopyrightDialog };
};
